import { useState } from "react";
import { Link } from "react-router-dom";
import { API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Mail, ArrowLeft, CheckCircle, Loader2 } from "lucide-react";

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await fetch(`${API}/auth/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim().toLowerCase() }),
      });
      setSent(true);
    } catch {
      setSent(true); // anti-enumeration: always show success
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0A1A4A] via-[#1E3A8A] to-[#0F1B3D] flex items-center justify-center px-4" data-testid="forgot-password-page">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-8">
        {sent ? (
          <div className="text-center space-y-4" data-testid="forgot-sent">
            <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto">
              <CheckCircle className="w-7 h-7 text-green-600" />
            </div>
            <h1 className="text-xl font-heading font-bold text-slate-900">Check your email</h1>
            <p className="text-sm text-slate-600">
              If an account exists for <strong>{email}</strong>, we've sent a password reset link.
              It expires in 1 hour. Be sure to check your spam folder.
            </p>
            <Link to="/login" className="inline-flex items-center gap-1.5 text-sm text-[#1E3A8A] hover:underline font-medium">
              <ArrowLeft className="w-4 h-4" /> Back to sign in
            </Link>
          </div>
        ) : (
          <>
            <h1 className="text-2xl font-heading font-bold text-slate-900 mb-1">Reset your password</h1>
            <p className="text-sm text-slate-500 mb-6">Enter your account email and we'll send you a secure reset link.</p>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label className="text-slate-600">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="input-light pl-10"
                    placeholder="you@example.com"
                    required
                    data-testid="forgot-email-input"
                  />
                </div>
              </div>
              <Button type="submit" disabled={loading} className="w-full btn-primary h-11 flex items-center justify-center gap-2" data-testid="forgot-submit">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send reset link"}
              </Button>
            </form>
            <div className="text-center mt-6">
              <Link to="/login" className="inline-flex items-center gap-1.5 text-sm text-[#1E3A8A] hover:underline font-medium">
                <ArrowLeft className="w-4 h-4" /> Back to sign in
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
